// SPDX-FileCopyrightText: Copyright Orangebot, Inc.
// SPDX-License-Identifier: Apache-2.0

/**
 * CallControls Component
 *
 * Control buttons for active calls - mute, camera toggle, screen share, end call.
 */

import { Group, ActionIcon, Tooltip } from '@mantine/core';
import {
  IconMicrophone,
  IconMicrophoneOff,
  IconVideo,
  IconVideoOff,
  IconScreenShare,
  IconScreenShareOff,
  IconPhone,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import classes from './CareCommPanel.module.css';

export interface CallControlsProps {
  /** Whether audio is muted */
  isMuted: boolean;
  /** Whether camera is off */
  isCameraOff: boolean;
  /** Whether screen sharing is active */
  isScreenSharing: boolean;
  /** Whether this is a video call (shows camera controls) */
  isVideoCall: boolean;
  /** Toggle mute callback */
  onToggleMute: () => void;
  /** Toggle camera callback */
  onToggleCamera: () => void;
  /** Toggle screen share callback */
  onToggleScreenShare: () => void;
  /** End call callback */
  onEndCall: () => void;
  /** Optional: disabled state */
  disabled?: boolean;
}

export function CallControls({
  isMuted,
  isCameraOff,
  isScreenSharing,
  isVideoCall,
  onToggleMute,
  onToggleCamera,
  onToggleScreenShare,
  onEndCall,
  disabled = false,
}: CallControlsProps) {
  const { t } = useTranslation();

  return (
    <Group justify="center" gap="lg">
      {/* Mute/Unmute */}
      <Tooltip label={isMuted ? t('carecomm.unmute', 'Unmute') : t('carecomm.mute', 'Mute')}>
        <ActionIcon
          size={56}
          radius="xl"
          variant={isMuted ? 'filled' : 'light'}
          color={isMuted ? 'red' : 'gray'}
          onClick={onToggleMute}
          disabled={disabled}
          className={classes.callControlButton}
          data-active={isMuted}
        >
          {isMuted ? (
            <IconMicrophoneOff size={24} color="white" />
          ) : (
            <IconMicrophone size={24} color="white" />
          )}
        </ActionIcon>
      </Tooltip>

      {/* Camera on/off (video calls only) */}
      {isVideoCall && (
        <Tooltip label={isCameraOff ? t('carecomm.cameraOn', 'Turn camera on') : t('carecomm.cameraOff', 'Turn camera off')}>
          <ActionIcon
            size={56}
            radius="xl"
            variant={isCameraOff ? 'filled' : 'light'}
            color={isCameraOff ? 'red' : 'gray'}
            onClick={onToggleCamera}
            disabled={disabled}
            className={classes.callControlButton}
            data-active={isCameraOff}
          >
            {isCameraOff ? (
              <IconVideoOff size={24} color="white" />
            ) : (
              <IconVideo size={24} color="white" />
            )}
          </ActionIcon>
        </Tooltip>
      )}

      {/* Screen share (video calls only) */}
      {isVideoCall && (
        <Tooltip label={isScreenSharing ? t('carecomm.stopSharing', 'Stop sharing') : t('carecomm.shareScreen', 'Share screen')}>
          <ActionIcon
            size={56}
            radius="xl"
            variant={isScreenSharing ? 'filled' : 'light'}
            color={isScreenSharing ? 'blue' : 'gray'}
            onClick={onToggleScreenShare}
            disabled={disabled}
            className={classes.callControlButton}
            data-active={isScreenSharing}
            data-sharing={isScreenSharing}
          >
            {isScreenSharing ? (
              <IconScreenShareOff size={24} color="white" />
            ) : (
              <IconScreenShare size={24} color="white" />
            )}
          </ActionIcon>
        </Tooltip>
      )}

      {/* End call */}
      <Tooltip label={t('carecomm.endCall', 'End call')}>
        <ActionIcon
          size={56}
          radius="xl"
          variant="filled"
          color="red"
          onClick={onEndCall}
          disabled={disabled}
          className={classes.endCallButton}
        >
          <IconPhone
            size={24}
            color="white"
            style={{ transform: 'rotate(135deg)' }}
          />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

export default CallControls;
